import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Stack,
  Divider,
  Tabs,
  Tab,
  Snackbar,
  Alert,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import TimelineIcon from "@mui/icons-material/Timeline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DateRangeIcon from "@mui/icons-material/DateRange";
import PeopleIcon from "@mui/icons-material/People";
import InventoryIcon from "@mui/icons-material/Inventory";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import MarketsDataByMonth from "../Components/MarketsDataByMonth";
import MarketsDataByDay from "../Components/MarketsDataByDay";
import MarketsDataByWeek from "../Components/MarketsDataByWeek";
import VillagerShopData from "../Components/VillagerShopData";
import PlayerMarketsDataTable from "../Components/PlayerMarketsDataTable";
import DailyStockReport from "../Components/DailyStockReport";
import MoneySupplyData from "../Components/MoneySupplyData";
import useParsedTSVCache from "../hooks/useParsedTSVCache";

/**
 * Lazy-loading tab panel - only mounts content when active
 * This prevents all tabs from rendering simultaneously and causing performance issues
 */
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`markets-tabpanel-${index}`}
      aria-labelledby={`markets-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `markets-tab-${index}`,
    "aria-controls": `markets-tabpanel-${index}`,
  };
}

// Tab name mapping
const TAB_NAMES = [
  "historical-weekly",
  "historical-monthly",
  "leaderboard",
  "stock-report",
  "money-supply",
  "villager-shops",
];

const TAB_MAP = {
  "historical-weekly": 0,
  "historical-monthly": 1,
  leaderboard: 2,
  "stock-report": 3,
  "money-supply": 4,
  "villager-shops": 5,
};

function Markets() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);

  // Use parsed TSV cache - eliminates need for manual PapaParse calls
  // Data is automatically parsed and cached as JavaScript objects
  const {
    data: marketData,
    loading: marketDataLoading,
    error: marketDataError,
  } = useParsedTSVCache(
    "markets_cache_all_time_avg",
    "/markets-data/latest-all-time-avg.tsv",
  );

  const {
    data: monthlyData,
    loading: monthlyDataLoading,
    error: monthlyDataError,
  } = useParsedTSVCache(
    "markets_cache_all_time_by_month",
    "/markets-data/latest-all-time-by-month.tsv",
  );

  // Memoize leaderboard index building - only rebuild when data actually changes
  // Structure: { ALL_TIME: [...], "2024-01": [...], "2024-02": [...], ... }
  const leaderboardIndex = useMemo(() => {
    if (marketDataLoading || monthlyDataLoading) return null;
    if (!marketData || !monthlyData) return null;
    if (marketData.length === 0 && monthlyData.length === 0) return null;

    const index = {};

    // Index ALL_TIME data (filter out empty item_type once)
    index.ALL_TIME = marketData.filter(
      (item) => item.item_type && item.item_type.toString().trim() !== "",
    );

    // Index monthly data by month
    monthlyData.forEach((item) => {
      if (!item.item_type || item.item_type.toString().trim() === "") return;
      const month = item.month;
      if (!month) return;

      if (!index[month]) {
        index[month] = [];
      }
      index[month].push(item);
    });

    return index;
  }, [marketData, monthlyData, marketDataLoading, monthlyDataLoading]);

  const [activeTab, setActiveTab] = useState(0);

  // Error Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Mark component as hydrated after first render to prevent hydration mismatches
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Initialize from URL after hydration completes to avoid SSR/client mismatch
  useEffect(() => {
    if (!isHydrated) return; // Skip during SSR and initial client render

    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = TAB_MAP[tabParam];
      if (tabIndex !== undefined) {
        setActiveTab(tabIndex);
      } else {
        // Invalid tab parameter - show error and fallback to default
        setSnackbarMessage(`Invalid tab "${tabParam}". Showing default tab.`);
        setSnackbarOpen(true);
        // Update URL to correct tab
        const newParams = new URLSearchParams(searchParams);
        newParams.set("tab", TAB_NAMES[0]);
        setSearchParams(newParams, { replace: true });
      }
    } else {
      // No tab parameter - set default in URL
      const newParams = new URLSearchParams(searchParams);
      newParams.set("tab", TAB_NAMES[0]);
      setSearchParams(newParams, { replace: true });
    }
  }, [isHydrated, searchParams, setSearchParams]); // Run after hydration flag is set

  // Data is now automatically parsed by useParsedTSVCache - no manual parsing needed!
  // Leaderboard index is now built with useMemo - no setTimeout needed!

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);

    // Create fresh params with only the new tab
    const newParams = new URLSearchParams();
    newParams.set("tab", TAB_NAMES[newValue]);

    // Preserve only the parameters relevant to the new tab
    switch (newValue) {
      case 0: // historical-weekly
        if (searchParams.get("weekly_item")) {
          newParams.set("weekly_item", searchParams.get("weekly_item"));
        }
        if (searchParams.get("weekly_time")) {
          newParams.set("weekly_time", searchParams.get("weekly_time"));
        }
        break;
      case 1: // historical-monthly
        if (searchParams.get("monthly_item")) {
          newParams.set("monthly_item", searchParams.get("monthly_item"));
        }
        if (searchParams.get("monthly_time")) {
          newParams.set("monthly_time", searchParams.get("monthly_time"));
        }
        break;
      case 2: // leaderboard
        if (searchParams.get("leaderboard_month")) {
          newParams.set(
            "leaderboard_month",
            searchParams.get("leaderboard_month"),
          );
        }
        if (searchParams.get("leaderboard_search")) {
          newParams.set(
            "leaderboard_search",
            searchParams.get("leaderboard_search"),
          );
        }
        if (searchParams.get("leaderboard_orderby")) {
          newParams.set(
            "leaderboard_orderby",
            searchParams.get("leaderboard_orderby"),
          );
        }
        if (searchParams.get("leaderboard_order")) {
          newParams.set(
            "leaderboard_order",
            searchParams.get("leaderboard_order"),
          );
        }
        break;
      case 3: // stock-report
        if (searchParams.get("stock_volume")) {
          newParams.set("stock_volume", searchParams.get("stock_volume"));
        }
        if (searchParams.get("stock_status")) {
          newParams.set("stock_status", searchParams.get("stock_status"));
        }
        if (searchParams.get("stock_search")) {
          newParams.set("stock_search", searchParams.get("stock_search"));
        }
        if (searchParams.get("stock_orderby")) {
          newParams.set("stock_orderby", searchParams.get("stock_orderby"));
        }
        if (searchParams.get("stock_order")) {
          newParams.set("stock_order", searchParams.get("stock_order"));
        }
        break;
      case 4: // money-supply
        if (searchParams.get("money_supply_time")) {
          newParams.set(
            "money_supply_time",
            searchParams.get("money_supply_time"),
          );
        }
        break;
      // case 5: villager-shops - no params to preserve
    }

    setSearchParams(newParams);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // Handle filter changes from child components
  // Each tab uses namespaced parameters to maintain independent filter state
  const handleWeeklyFilterChange = (filters) => {
    const newParams = new URLSearchParams(searchParams);
    if (filters.item) {
      newParams.set("weekly_item", filters.item);
    }
    if (filters.time) {
      newParams.set("weekly_time", filters.time);
    }
    setSearchParams(newParams);
  };

  const handleMonthlyFilterChange = (filters) => {
    const newParams = new URLSearchParams(searchParams);
    if (filters.item) {
      newParams.set("monthly_item", filters.item);
    }
    if (filters.time) {
      newParams.set("monthly_time", filters.time);
    }
    setSearchParams(newParams);
  };

  const handleLeaderboardFilterChange = (filters) => {
    const newParams = new URLSearchParams(searchParams);
    if (filters.month !== undefined) {
      newParams.set("leaderboard_month", filters.month);
    }
    if (filters.search !== undefined) {
      if (filters.search) {
        newParams.set("leaderboard_search", filters.search);
      } else {
        newParams.delete("leaderboard_search");
      }
    }
    if (filters.orderby !== undefined) {
      newParams.set("leaderboard_orderby", filters.orderby);
    }
    if (filters.order !== undefined) {
      newParams.set("leaderboard_order", filters.order);
    }
    setSearchParams(newParams);
  };

  const handleStockReportFilterChange = (filters) => {
    const newParams = new URLSearchParams(searchParams);
    if (filters.volume !== undefined) {
      newParams.set("stock_volume", filters.volume);
    }
    if (filters.status !== undefined) {
      newParams.set("stock_status", filters.status);
    }
    if (filters.search !== undefined) {
      if (filters.search) {
        newParams.set("stock_search", filters.search);
      } else {
        newParams.delete("stock_search");
      }
    }
    if (filters.orderby !== undefined) {
      newParams.set("stock_orderby", filters.orderby);
    }
    if (filters.order !== undefined) {
      newParams.set("stock_order", filters.order);
    }
    setSearchParams(newParams);
  };

  const handleMoneySupplyFilterChange = (filters) => {
    const newParams = new URLSearchParams(searchParams);
    if (filters.time !== undefined) {
      newParams.set("money_supply_time", filters.time);
    }
    setSearchParams(newParams);
  };

  return (
    <Box sx={{ flexGrow: 1, py: 4 }}>
      <Container maxWidth="lg">
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
            <StorefrontIcon fontSize="large" color="primary" />
            <Box>
              <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
                Markets Info
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Player Mined and Traded Blocks Data
              </Typography>
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />

          <Typography variant="body1" gutterBottom>
            If you are looking to buy blocks on MCVerse, you'll have to find a
            player who is selling it. It's a bit of a grind, yes!
            <br />
            <br />
            Markets allow sellers to sell in bulk while they are offline.
            <br />
            <br />
            Villagers Shops can act as Buyers/Sellers in your claims.
            <br />
            <br />
            Stats were generated 2026-03-23.
          </Typography>
        </Paper>

        <Paper elevation={3} sx={{ mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="markets navigation tabs"
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                minHeight: 64,
                textTransform: "none",
                fontSize: "1rem",
              },
            }}
          >
            {/* <Tab
              icon={<CalendarTodayIcon />}
              iconPosition="start"
              label="Historical - Daily"
              {...a11yProps(0)}
            /> */}
            <Tab
              icon={<DateRangeIcon />}
              iconPosition="start"
              label="Historical - Weekly"
              {...a11yProps(0)}
            />
            <Tab
              icon={<TimelineIcon />}
              iconPosition="start"
              label="Historical - Monthly"
              {...a11yProps(1)}
            />
            <Tab
              icon={<LeaderboardIcon />}
              iconPosition="start"
              label="Leaderboard"
              {...a11yProps(2)}
            />
            <Tab
              icon={<InventoryIcon />}
              iconPosition="start"
              label="Stock Report"
              {...a11yProps(3)}
            />
            <Tab
              icon={<AccountBalanceIcon />}
              iconPosition="start"
              label="Money Supply"
              {...a11yProps(4)}
            />
            <Tab
              icon={<PeopleIcon />}
              iconPosition="start"
              label="Villager Shops"
              {...a11yProps(5)}
            />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* <TabPanel value={activeTab} index={0}>
              <MarketsDataByDay
                title="Daily Market Trends"
                subtitle="Last 30 days of market activity"
              />
            </TabPanel> */}

            <TabPanel value={activeTab} index={0}>
              <MarketsDataByWeek
                title="Weekly Market Trends"
                subtitle="Weekly aggregated market activity"
                initialItemType={searchParams.get("weekly_item")}
                initialTimeFilter={searchParams.get("weekly_time")}
                onFilterChange={handleWeeklyFilterChange}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <MarketsDataByMonth
                monthlyData={monthlyData}
                loading={monthlyDataLoading}
                title="Market Data Stats"
                subtitle="Monthly market trends"
                initialItemType={searchParams.get("monthly_item")}
                initialTimeFilter={searchParams.get("monthly_time")}
                onFilterChange={handleMonthlyFilterChange}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <PlayerMarketsDataTable
                marketData={marketData}
                monthlyData={monthlyData}
                loading={marketDataLoading}
                monthlyLoading={monthlyDataLoading}
                error={marketDataError}
                dataIndex={leaderboardIndex}
                title="Player Markets Data"
                initialMonth={searchParams.get("leaderboard_month")}
                initialSearch={searchParams.get("leaderboard_search")}
                initialOrderBy={searchParams.get("leaderboard_orderby")}
                initialOrder={searchParams.get("leaderboard_order")}
                onFilterChange={handleLeaderboardFilterChange}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
              <DailyStockReport
                title="Daily Stock Report"
                subtitle="Current inventory status and supply metrics"
                initialVolumeFilter={searchParams.get("stock_volume")}
                initialStatusFilter={searchParams.get("stock_status")}
                initialSearch={searchParams.get("stock_search")}
                initialOrderBy={searchParams.get("stock_orderby")}
                initialOrder={searchParams.get("stock_order")}
                onFilterChange={handleStockReportFilterChange}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={4}>
              <MoneySupplyData
                title="Money Supply Metrics"
                subtitle="Economy-wide monetary flows and transaction volumes"
                initialTimeFilter={searchParams.get("money_supply_time")}
                onFilterChange={handleMoneySupplyFilterChange}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={5}>
              <VillagerShopData />
            </TabPanel>
          </Box>
        </Paper>

        {/* Error Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity="warning"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}

export default Markets;
